import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Inject,
  PLATFORM_ID
} from '@angular/core';

import {
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet
} from '@angular/router';

import { filter, Subscription } from 'rxjs';
import { ChunkErrorHandlerService } from '../shared/services/chunk-error-handler.service';
import { CacheService } from '../shared/services/cache.service';
import { BlobService } from '../shared/services/blob.service';
import { VisitorSessionService } from '../shared/services/visitor-session.service';
import { AuthService } from './auth/services/auth.service';
import { AppRoutesUtils } from '../utils/app-routes-util';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'] // ✅ FIXED
})
export class App implements OnInit, OnDestroy {

  protected readonly title = signal('StockIcon');

  private isBrowser: boolean;
  private routerSub = new Subscription();
  private intervalId: number | null = null;
  private chunkErrorHandler = inject(ChunkErrorHandlerService);

  constructor(
    private router: Router,
    private cacheService: CacheService,
    private blobService: BlobService,
    private visitorSessionService: VisitorSessionService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // ✅ Only setup in browser
    if (this.isBrowser) {
      this.setupChunkErrorHandlers();
    }
  }

  // ============================
  // 🔥 CHUNK ERROR HANDLING
  // ============================

  private setupChunkErrorHandlers(): void {

    // Unhandled Promise Rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const message = error?.message || error?.toString() || '';

      if (this.isChunkError(message)) {
        event.preventDefault();
        this.chunkErrorHandler.handleChunkError(
          error instanceof Error ? error : new Error(message),
          message
        );
      }
    });

    // General Errors
    window.addEventListener('error', (event: ErrorEvent) => {
      const error = event.error || new Error(event.message);
      const message = error?.message || event.message || '';

      if (this.isChunkError(message)) {
        event.preventDefault();
        this.chunkErrorHandler.handleChunkError(error, message);
      }
    }, true);
  }

  private isChunkError(message: string): boolean {
    return (
      message.includes('chunk') ||
      message.includes('Loading chunk') ||
      message.includes('Failed to fetch dynamically imported module')
    );
  }

  // ============================
  // 🔥 INIT
  // ============================

  ngOnInit(): void {

    AppRoutesUtils.initialize(this.router);

    // ✅ Works in SSR + Browser
    this.routerSub.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.blobService.refreshTokenIfNeeded().catch(() => {});
          this.blobService.refreshCoolTokenIfNeeded().catch(() => {});
          this.authService.refreshAuthTokenIfNeeded().catch(() => {});
          if (this.isBrowser) {
            this.visitorSessionService.trackScreen(
              event.urlAfterRedirects || event.url
            );
          }
        })
    );

    // ✅ Browser-only: blob SAS auto-refresh lives in BlobService (SSR-safe).
    // Keep only JWT refresh here.
    if (this.isBrowser) {
      this.blobService.startAutoRefresh();

      const intervalMs = 1 * 60 * 1000;
      this.intervalId = window.setInterval(() => {
        this.authService.refreshAuthTokenIfNeeded().catch(() => {});
      }, intervalMs);

      this.authService.refreshAuthTokenIfNeeded().catch(() => {});
    }

    // ✅ Router error handling (browser only)
    if (this.isBrowser) {
      this.routerSub.add(
        this.router.events
          .pipe(filter(event => event instanceof NavigationError))
          .subscribe((event: any) => {
            const error = event.error;
            const message = error?.message || '';

            if (this.isChunkError(message)) {
              this.chunkErrorHandler.handleChunkError(error, message);
            }
          })
      );
    }

    // Warm caches without blocking bootstrap (child routes subscribe independently).
    this.routerSub.add(this.cacheService.getCategoriesFromCache().subscribe());
    this.routerSub.add(this.cacheService.geStylesFromCache().subscribe());
    this.routerSub.add(this.cacheService.getAssetCategoriesFromCache().subscribe());

    // Browser/IP info for LogSession headers (do not block visitor tracking on ipify).
    if (this.isBrowser) {
      this.routerSub.add(this.cacheService.getBrowserInfoFromCache().subscribe());
      this.visitorSessionService.trackVisit();
    }
  }

  // ============================
  // 🔥 DESTROY
  // ============================

  ngOnDestroy(): void {
    this.routerSub.unsubscribe();

    if (this.isBrowser && this.intervalId) {
      window.clearInterval(this.intervalId);
    }
  }
}